/* eslint-disable react/function-component-definition */
/* Global imports. */
import React from "react";
import { RichTextElement, ImageElement } from "@kontent-ai/gatsby-components";
import { Link } from "gatsby"
import { nonPanMacFormat } from "@src/constants";

/* Model imports. */
import {
  KontentArticleSummary,
  KontentBanner,
  KontentBannerCarousel,
  KontentBannerHeader,
  KontentBookSpotlight,
  KontentBookSpotlightCollection,
  KontentButton,
  KontentColumns,
  KontentContributorSpotlightCollection,
  KontentDownload,
  KontentEmbed,
  KontentEvent,
  KontentFAQList,
  KontentFeaturedArticleCollection,
  KontentFeaturedResourceCollection,
  KontentImage,
  KontentInstagramEmbed,
  KontentIsbnList,
  KontentProductCollection,
  KontentQuote,
  KontentRecipe,
  KontentReusableRichText,
  KontentSoundCloudEmbed,
  KontentTwitterTimeline,
  KontentYouTubeCollection,
  KontentYouTubeEmbed,
} from "@models/kontent";

/* Component imports. */
import AnchorLinks from "@components/AnchorLinks";
import ArticleCard from "@components/ArticleCard";
import Banner from "@components/Banner";
import BannerCarousel from "@components/BannerCarousel";
import BannerCollection from "@components/BannerCollection";
import BannerHeader from "@components/BannerHeader";
import Button from "@components/Button";
import BookSpotlight from "@components/BookSpotlight";
import BookSpotlightCollection from "@components/BookSpotlightCollection";
import Columns from "@components/Columns";
import Contact from "@components/Contact";
import ContactsGroup from "@components/ContactsGroup";
import ContributorCollection from "@components/ContributorSpotlightCollection";
import ContributorSpotlight from "@components/ContributorSpotlight";
import DontMiss from "@src/components/DontMiss";
import Download from "@components/Download";
import EditionCollection from "@components/EditionCollection";
import Embed from "@components/Embed";
import EventCard from "@components/EventCard";
import EventCollection from "@components/EventCollection";
import FAQ from "@components/FAQ";
import FeaturedArticles from "@components/FeaturedArticles";
import FeaturedContent from "@components/FeaturedContent";
import FeaturedResources from "@components/FeaturedResources";
import Image from "@components/Image";
import InstagramEmbed from "@components/InstagramEmbed";
import NewsletterHelper from "@components/NewsletterHelper";
import Quote from "@components/Quote";
import ProductCollection from "@components/ProductCollection";
import Recipe from "@components/Recipe";
import ReusableRichText from "@components/ReusableRichText";
import SectionBreak from "@components/SectionBreak";
import SoundCloudEmbed from "@components/SoundCloudEmbed";
import TwitterTimeline from "@components/TwitterTimeline";
import YouTubeCollection from "@components/YouTubeCollection";
import YouTubeEmbed from "@components/YouTubeEmbed";

/* Other imports. */
import { kontent } from "@src/constants";
import { renderCodeBlock, filterEditions, isPreOrder, FilterEditionsByCollectionIdentifier, FilterEditionsByIsPublished } from "@src/helpers";
import { convertKontentImageURLToImageKitURL, cleanImagePathForImageKit } from "@src/imagekit";

import KontentFieldRichText from "@models/kontent/KontentFieldRichText"
import { PanMacmillanEdition } from "@src/models/panmacmillan";

interface Props {
  value: KontentFieldRichText
  editions?: Array<PanMacmillanEdition>
  resources?: Array<KontentDownload>
  referringSlug? : string
  isQuickPreview?: boolean 
  maxImageWidth?: number
}

function getNumberOfColumns(numberOfColumnsValue:string) {
  switch(numberOfColumnsValue) {
    case "n2":
     return 2;
    case "n3":
     return 3;
    case "n4":
     return 4;
    default:
      return 1
  }
}

function ResolveArticleCategoryToSlug(category_codename) {
  const lifeStyleCodename = "lifestyle___wellbeing"
  const lifeStyleSlug = "lifestyle-wellbeing"
  const youngAdultCodename = "young_adult"
  const youngAdultSlug = "young-adult"
  const classicsCodename = "classics"
  const classicsSlug = "classics"
  const generalCodename = "general"
  const generalSlug = "general"
  const childrenCodename = "books_for_children"
  const childrenSlug = "books-for-children"
  const fictionCodename = "fiction"
  const fictionSlug = "fiction"
  const literaryCodename = "literary"
  const literarySlug = "literary" 
  const scienceFictionFantasyCodename = "science_fiction___fantasy"
  const scienceFictionFantasySlug = "science-fiction-and-fantasy"  
  const crimeThrillerCodename = "crime___thrillers"
  const crimeThrillerSlug = "crime-thriller"  
  const historyCodename = "history"
  const historySlug = "history" 
  
  switch(category_codename) {
    case lifeStyleCodename:
      return lifeStyleSlug
    case youngAdultCodename:
      return youngAdultSlug
    case classicsCodename:
      return classicsSlug
    case generalCodename:
      return generalSlug
    case childrenCodename:
      return childrenSlug
    case fictionCodename:
      return fictionSlug
    case literaryCodename:
      return literarySlug
    case scienceFictionFantasyCodename:
      return scienceFictionFantasySlug
    case crimeThrillerCodename:
      return crimeThrillerSlug 
    case historyCodename:
      return historySlug                      
  }
}

const KontentRichText = ({ value, editions, resources, referringSlug, isQuickPreview, maxImageWidth }: Props) => <RichTextElement
      value={value.value}
      images={value.images}
      links={value.links}
      linkedItems={value.modular_content ? value.modular_content : value.linkedItems}
      resolveImage={image => {
        image.url = convertKontentImageURLToImageKitURL(image.url, image.width)
        return <ImageElement
        image={image}
        backgroundColor="#ccc"
        width={maxImageWidth}
        height={image.height}
        alt={image.description ? image.description : image.name}
      />
      }}
      resolveLink={(link, domNode) => {
        return (
          <Link to={`/${link.type}/${link.url_slug}`}>
            {domNode.children[0].data}
          </Link>
        )
      }}
      resolveLinkedItem={linkedItem => {
	  
        switch (linkedItem.system.type) {
          case kontent.components.types.anchorlinks : {
            return <AnchorLinks 
              title={linkedItem.elements && linkedItem.elements.title && linkedItem.elements.title.value} 
              shortTitle={linkedItem.elements && linkedItem.elements.short_title && linkedItem.elements.short_title.value}
            />
          }
          case kontent.components.types.banner: {
            const item: KontentBanner = linkedItem.elements;
            const styling = item.styling.value[0] && item.styling.value[0].elements
            const backgroundColour = styling && styling.background_colour.value ? styling.background_colour.value : "transparent" 
            const stylingTextColour= styling && styling.primary_colour.value ? styling.primary_colour.value :  ""
            const bannerTextColour= item.text_colour && item.text_colour.value

            return (
              <Banner 
                title={item.title.value} 
                text={item.text.value} 
                buttonText={item.button_text.value} 
                buttonUrl={item.button_url.value} 
                imageUrl={item.background_image.value && item.background_image.value[0] && convertKontentImageURLToImageKitURL(cleanImagePathForImageKit(item.background_image.value[0].url),1250)}
                imageLinkURL={item.background_image_link ? item.background_image_link.value : ""}
                textPlacementClass={item.text_placement.value && item.text_placement.value[0] ? `banner_text_${item.text_placement.value[0].codename}` : ""}
                backgroundColour={item.styling.value[0] && item.styling.value[0].elements.background_colour.value ? item.styling.value[0].elements.background_colour.value :  "#FFFFFF"}
                primaryTextColour = {bannerTextColour ? bannerTextColour : stylingTextColour}
                bannerType={item.type && item.type.value && item.type.value[0] ? item.type.value[0].codename : "background_image"}
                bannerWidth={item.width && item.width.value && item.width.value[0] && item.width.value[0].codename}
                backgroundColourLeft={item.background_colour_left && item.background_colour_left.value ? item.background_colour_left.value : backgroundColour}
                backgroundColourRight={item.background_colour_right && item.background_colour_right.value ? item.background_colour_right.value : backgroundColour}
                isPageHero={item.page_hero && item.page_hero.value && item.page_hero.value[0] && item.page_hero.value[0].codename === "yes"}
                imagePlacement={item.background_image_placement && item.background_image_placement.value && item.background_image_placement.value[0] ? item.background_image_placement.value[0].codename : 'left'}
                componentID={item.component_id && item.component_id.value}
                referringSlug={referringSlug}
              />
            );
          }
          case kontent.components.types.bannercarousel : {
            const item: KontentBannerCarousel = linkedItem.elements;
            return <BannerCarousel 
              banners={item.banners.value} 
              carouselButtonColour={item.carousel_button_colour.value[0].codename} 
              carouselControlsBackgroundColour={item.carousel_controls_background_colour.value}
              autoplay={item.carousel_autoplay?.value[0]?.codename === "yes" ? true : false}
            />
          }
          case kontent.components.types.bannercollection : {
            return <BannerCollection banners={linkedItem.elements} />
          }
          case kontent.components.types.bannerheader : {
            const item: KontentBannerHeader = linkedItem.elements;
            return <BannerHeader 
              title={item && item.title && item.title.value} 
              intro={item && item.introduction && item.introduction.value} 
              image={item && item.image.value && item.image.value[0].url} 
              imageAlt={item && item.image.value && item.image.value[0].description}
              displayKeyline={item && item.display_keyline.value[0] && item.display_keyline.value[0].codename === "yes"}
              relatedItems={item && item.related_items && item.related_items.value}
            />
          }
          case kontent.components.types.bookSpotlight: {
            const item: KontentBookSpotlight = linkedItem.elements;
            const tempEditions = editions;
            const primaryEditionLogicFormatBlacklist = new Array ("EA","EB","EC","ED","AA","AB","AC","AJ","AN","AO","AZ")
            const forceSpecifiedEdition = item.disable_primary_edition_logic.value && item.disable_primary_edition_logic.value[0] && item.disable_primary_edition_logic.value[0].codename && item.disable_primary_edition_logic.value[0].codename === "true" ? true : false;

            const filteredEdition: PanMacmillanEdition = tempEditions && tempEditions.filter(function(edition) { 
              return edition.node.isbn === item.isbn.value;
            });

            const getPrimaryEdition = () => {
              const primaryEditions = filteredEdition && filteredEdition[0] && filteredEdition[0].node.alternateEditions.filter(function(edition) { 
                return edition.isPrimaryEdition === true;
              });

              return primaryEditions ? primaryEditions[0] : null
            }

            const filteredSpecifiedEdition = filteredEdition && filteredEdition[0] && filteredEdition[0].node
            const bookData = filteredSpecifiedEdition && primaryEditionLogicFormatBlacklist.includes(filteredSpecifiedEdition.format) || forceSpecifiedEdition || filteredSpecifiedEdition && filteredSpecifiedEdition.isPrimaryEdition || filteredSpecifiedEdition && filteredSpecifiedEdition.audienceRange[0] && filteredSpecifiedEdition.audienceRange[0].audienceRangeBucket <= 12  ? filteredSpecifiedEdition : getPrimaryEdition() ;
            const altJacketImageUrl = item && item.alternative_jacket_image && item.alternative_jacket_image.value && item.alternative_jacket_image.value[0]  && item.alternative_jacket_image.value[0].url
            const altJacketImageKontent = item && item.alternative_jacket_image && item.alternative_jacket_image.value && item.alternative_jacket_image.value[0]  && item.alternative_jacket_image.value[0]
            const customAuthorName = item && item.contributor_name && item.contributor_name.value
            const customDescription = item.alternative_book_description && item.alternative_book_description.value && item.alternative_book_description.value
            const bookDataJacketImage = bookData && bookData.sourceImagePath;
            const primaryAuthorName = customAuthorName ? customAuthorName : bookData && bookData.contributors && bookData.contributors[0] && bookData.contributors[0].contributorDetails && bookData.contributors[0].contributorDetails[0].personName
            const bookDataDescription = bookData && bookData.textContents && bookData.textContents.filter(textContents => textContents.textType === "3")
            const isPanMacTitle = item.is_this_a_pan_macmillan_book_.value && item.is_this_a_pan_macmillan_book_.value[0] && item.is_this_a_pan_macmillan_book_.value[0].codename && item.is_this_a_pan_macmillan_book_.value[0].codename === "no" ? false : true;
        

            return (
              <BookSpotlight 
              title={item.title.value ? item.title.value : bookData && bookData.titleFull}
              isbn={bookData && bookData.isbn ? bookData.isbn : item.isbn.value} 
              primaryAuthorName={primaryAuthorName ? primaryAuthorName : bookData && bookData.primaryContributorName}
              isPanMacmillanTitle={isPanMacTitle ? "yes" : "no"} 
              altJacket={altJacketImageUrl}
              kontentImage={altJacketImageKontent}
              format={ isPanMacTitle ? bookData && bookData.format : nonPanMacFormat}
              customUrl={item.custom_url.value}
              jacketImagePath={bookDataJacketImage}
              localImagePath={""}
              description={customDescription && customDescription !== "<p><br></p>" ? customDescription : bookDataDescription && bookDataDescription[0] && bookDataDescription[0].text}
              isPreOrder={bookData && bookData.publicationDate && isPreOrder(new Date(bookData.publicationDate))}
              editionUrl= {bookData && bookData.fields && bookData.fields.slug && `/${bookData.fields.slug}`}
              authorUrl={bookData && bookData.contributors && bookData.contributors[0] && bookData.contributors[0].contributorDetails && bookData.contributors[0].contributorDetails[0].slug && `/${bookData.contributors[0].contributorDetails[0].slug}`}
              jacketWidth={bookData && bookData.dimensions.width}
              jacketHeight={bookData && bookData.dimensions.height}
              imprint={bookData && bookData.imprintName ? bookData.imprintName : "Unknown"}
              referringSlug={`/${referringSlug}`}
              isQuickPreview={isQuickPreview}
              customURL={item.custom_url.value}
              />
            );
          }
          case kontent.components.types.bookSpotlightCollection: {
            const item: KontentBookSpotlightCollection = linkedItem.elements;
            
            return (
              <BookSpotlightCollection 
                title={item.title.value}
                introduction={item.introduction.value}
                bookSpotlights={isQuickPreview ? item.book_spotlights.linkedItems : item.book_spotlights.value}
                itemsPerRow={item.books_per_row.value && item.books_per_row.value[0] && item.books_per_row.value[0].name}
                displayDescription={item.elements.value.some(e => e.codename === 'description')}
                displayBuyButton={item.elements.value.some(e => e.codename === 'buy_now_link')}
                displayAuthor={item.elements.value.some(e => e.codename === 'author')}
                editions={editions}
                componentID={item.component_id.value}
                referringSlug={`/${referringSlug}`}
              />
            );
          }
          case kontent.components.types.button: {
            const item: KontentButton = linkedItem.elements;
            return (
              <Button
                text={item.button_text.value}
                link={item.button_link.value}
                alignment={item.alignment.value[0].codename}
              />
            );
          }
          case kontent.components.types.columns: {
            const item: KontentColumns = linkedItem.elements;

            return <Columns 
              numberOfColumns={getNumberOfColumns(item.number_of_columns && item.number_of_columns.value && item.number_of_columns.value[0] && item.number_of_columns.value[0].codename)}
              col1Content={item.column_1_content} 
              col2Content={item.column_2_content} 
              col3Content={item.column_3_content} 
              col4Content={item.column_4_content}
              reverseOrderSmallScreen={item.reverse_small_screen_order.value[0] && item.reverse_small_screen_order.value[0].codename === "yes" ? true : false}
            />
          }
          case kontent.components.types.contact: {
            return <ul className="location-grid !pl-0"><Contact linkedItem={linkedItem} /></ul>
          }
          case kontent.components.types.contactsGroup: {
            return <ContactsGroup linkedItem={linkedItem} />
          }
          case kontent.components.types.contributorSpotlight: {
            const item = linkedItem.elements;
            return (
              <ContributorSpotlight 
                kontentImage={item && item.image && item.image.value && item.image.value[0]} 
                remoteImage={item.remoteImage} 
                contributorName={item && item.name ? item.name.value : item && item.personName} 
                contributorId={item && item.contributor_id ? item.contributor_id.value : item && item.contributorID} 
                description={item && item.biography} 
                linkText={item && item.link_text ? item.link_text.value : "Find out more"} 
                linkUrl={item && item.link_url && item.link_url.value}
              />
            );
          }
          case kontent.components.types.contributorSpotlightCollection: {
            const item: (KontentContributorSpotlightCollection) = linkedItem.elements;
      
            return <ContributorCollection contributors={item} />;
          }
          case kontent.components.types.download: {
            const item: KontentDownload = linkedItem.elements;
            return (
              <Download
                title={item.title.value}
                description={item.description.value}
                resource={item.resource.value[0]}
                image={item.image.value[0]}
                buttonText={item.button_text.value}
                hasKeyline={item && item.display_keyline && item.display_keyline.value[0] && item.display_keyline.value[0].codename === "yes"}
                referringSlug={referringSlug}
              />
            );
          }
          case kontent.components.types.dontmiss: {
            const item: KontentDontMiss = linkedItem.elements;
            const articleSummary: KontentArticleSummary = item.article && item.article.value && item.article.value[0] && item.article.value[0].elements && item.article.value[0].fields ? item.article && item.article.value[0] && item.article.value[0] : item.article && item.article.linkedItems && item.article.linkedItems[0];
            const articleCategory = articleSummary && articleSummary.elements.article_categories && articleSummary.elements.article_categories.value[0] && ResolveArticleCategoryToSlug(articleSummary.elements.article_categories.value[0].codename)

            const getArticleSlug = (articleSummary, articleCategory) => {
              return articleSummary && articleSummary.fields ? articleSummary.fields.slug : `blogs/${articleCategory}/${articleSummary.elements.url_slug && articleSummary.elements.url_slug.value}`;
            }

            const getArticleImage = (articleSummary) => {
              return articleSummary && articleSummary.elements.image.value[0] && convertKontentImageURLToImageKitURL(cleanImagePathForImageKit(articleSummary.elements.image.value[0].url))
            }
            
            return (
              <DontMiss 
                title={item.title.value ? item.title.value : articleSummary.elements.title.value} 
                imageUrl={(item.image.value && item.image.value[0]) ? convertKontentImageURLToImageKitURL(cleanImagePathForImageKit(item.image.value[0].url)) : getArticleImage(articleSummary)}
                link={item.link && item.link.value !== '' ? item.link.value : articleSummary && `/${getArticleSlug(articleSummary, articleCategory)}`}
                linkText={item && item.link_text && item.link_text.value}
                componentHeading={item && item.component_heading && item.component_heading.value ? item.component_heading.value : "Don't Miss"}
                hideImage={item && item.hide_image && item.hide_image.value && item.hide_image.value[0] && item.hide_image.value[0].codename === "yes" ? true : false}
                componentID={item.component_id.value}
                referringSlug={referringSlug}
              />
            );
          }
          case kontent.components.types.embed: {
            const item: KontentEmbed = linkedItem.elements;
            return (
              <Embed html={item.html_code.value} script={item.script_tag_external_link.value} />
            );
          }
          case kontent.components.types.event: {
            const item: KontentEvent = linkedItem.elements;
            return <ol className="event-list js-event-list !ml-0 !list-none">
              <EventCard event={item} />
            </ol>
          }
          case kontent.components.types.eventCollection: {
            const item: Array<KontentEvent> = linkedItem.elements.events.value;
            return <EventCollection events={item} />
          }
          case kontent.components.types.faq: {
            
            const list: KontentFAQList = isQuickPreview ?  linkedItem.elements.items.linkedItems  : linkedItem.elements.items.value;

            return <FAQ items={list} />;
          } 
          case kontent.components.types.featuredArticles: {
            const collection: KontentFeaturedArticleCollection = linkedItem && linkedItem.elements;
            return (
              <FeaturedArticles
                title={collection.title && collection.title.value}
                subtitle={collection.subtitle && collection.subtitle.value}
                subcopy={collection.subcopy && collection.subcopy.value}
                format={collection.format && collection.format.value && collection.format.value[0] && collection.format.value[0].codename}
                articleSummaries={collection.featured_articles && collection.featured_articles.value}
                backgroundColor={collection.background_colour && collection.background_colour.value}
                textColour={collection.text_colour && collection.text_colour.value}
                textAlign={collection.text_align && collection.text_align.value && collection.text_align.value[0] && collection.text_align.value[0].codename}
                componentID={collection.component_id.value}
                referringSlug={referringSlug}
              />
            );
          }
          case kontent.components.types.featuredContent : {
            const item: any = linkedItem.elements;
            const featuredContentType = item.type.value[0] && item.type.value[0].codename
            const article = item.article && item.article.value[0];
            const displayWidth = item.display_width && item.display_width.value[0] && item.display_width.value[0].codename

            if(displayWidth === "flexible" || displayWidth === "constrained") {
              return <FeaturedContent componentID={item.component_id.value}
              featuredContent={item} article={article && article.elements} articleSlug={article && article.fields && article.fields.slug} />
            } else {
              return <section>
                <ul className="flex flex-col w-full gap-8 !pl-0 !list-none">
                  {(featuredContentType === "page" || article) && <ArticleCard 
                    articleSummary={article && article.elements} 
                    articleURL={article.value && article.value[0] && article.value[0].fields && `/${article.value[0].fields.slug}`} 
                    format={article.format && article.format.value[0] && article.format.value[0].codename ? article.format.value[0].codename : "landscape" } 
                    referringSlug={referringSlug}
                  />}
                </ul>
              </section>
            }
          }
          case kontent.components.types.featuredResources: {
            const collection: KontentFeaturedResourceCollection = linkedItem.elements;
            const handPickedResources = collection.handpick_resources && collection.handpick_resources.value
            let selectedResources = handPickedResources.length > 0 ? handPickedResources : GetFilteredResources()
            
            function GetFilteredResources() {
              let filteredResources = [];
              const relatedContributors = collection.related_contributors.value
              const relatedIsbns = collection.related_isbns.value
              const relatedSeries = collection.related_series.value
              const resourceContributorArray = relatedContributors && relatedContributors.replace(/\s/g, "").split(',')
              const resourceIsbnsArray = relatedIsbns && relatedIsbns.replace(/\s/g, "").split(',')
              const resourceSeriesArray = relatedSeries && relatedSeries.replace(/\s/g, "").split(',')
              const featuredResourcesTaxArray = collection.resources.value
          
              let imprintArray = []
              collection.imprint.value && collection.imprint.value.forEach(imprint => imprintArray.push(imprint.name))
          
              if(resourceContributorArray.length > 0 || resourceIsbnsArray.length > 0 || resourceSeriesArray.length > 0 || imprintArray.length > 0 || featuredResourcesTaxArray.length > 0) {
                filteredResources = resources.nodes ? resources.nodes : resources
          
                if(resourceContributorArray.length > 0) {
                  filteredResources = filteredResources.filter(resource => resourceContributorArray.includes(resource.elements.related_contributors.value))
                }
            
                if(resourceIsbnsArray.length > 0) {
                  filteredResources = filteredResources.filter(resource => resourceIsbnsArray.includes(resource.elements.related_isbns.value))
                }
            
                if(resourceSeriesArray.length > 0) {
                  filteredResources = filteredResources.filter(resource => resourceSeriesArray.includes(resource.elements.related_series.value))
                }
            
                if(imprintArray.length > 0) {
                    filteredResources = filteredResources.filter(resource => imprintArray.some(r=> resource.elements.imprint.value.some(e => e.name === r)))
                }
          
                if(featuredResourcesTaxArray.length > 0) {
                  filteredResources = filteredResources.filter(resource => featuredResourcesTaxArray.every(r=> resource.elements && resource.elements.resources.value.some(e => e.codename === r.codename)))
                }
              }
          
              return filteredResources
            }
            
            return (
              <FeaturedResources
                title={collection.title && collection.title.value}
                subtitle={collection.subtitle && collection.subtitle.value}
                subcopy={collection.subcopy && collection.subcopy.value}
                buttonText={collection.button_text && collection.button_text.value}
                buttonLink={collection.button_link && collection.button_link.value}
                resources={selectedResources}
                relatedContributors={collection.related_contributors && collection.related_contributors.value}
                relatedISBNs={collection.related_isbns && collection.related_isbns.value}
                relatedSeries={collection.related_contributors && collection.related_contributors.value}
               // imprints={}
              />
            );
          }
          case kontent.components.types.image: {
            const item: KontentImage = linkedItem.elements;
            // Resolve image urls to imagekit urls
            let KontentImageKit = item
            if(item && item.image && item.image.value && item.image.value[0] && item.image.value[0].url) {
                item.image.value[0].url = convertKontentImageURLToImageKitURL(cleanImagePathForImageKit(item.image.value[0].url))
            }

            return (
              <Image
                url={item && item.image && item.image.value && item.image.value[0] && item.image.value[0].url}
                kontentImage={KontentImageKit}
                description={item && item.image && item.image.value && item.image.value[0] && item.image.value[0].description}
                caption={item && item.caption && item.caption.value}
                alignment={item && item.alignment && item.alignment.value && item.alignment.value[0] && item.alignment.value[0].codename}
                ratio={item && item.image_ratio && item.image_ratio.value && item.image_ratio.value[0] && item.image_ratio.value[0].codename}
                maxWidth={maxImageWidth}
                linkUrl={item && item.link && item.link.value}
              />
            );
          }
          case kontent.components.types.instagramEmbed: {
            const item: KontentInstagramEmbed = linkedItem.elements;
            return <InstagramEmbed 
              html={item && item.instagram_embed_code && item.instagram_embed_code.value} 
              title={item && item.title && item.title.value}
              />;
          }
          case kontent.components.types.isbnList: {
            const item: KontentIsbnList = linkedItem.elements;

            const bookArray = item.isbns && item.isbns.value
            const contributorArray = item.contributor_s_ && item.contributor_s_.value
            const imprintArray = item.imprint && item.imprint.value
            const formatArray =  item.format_codes && item.format_codes.value
			const seriesID = item.series_id && item.series_id.value
            let filteredEditions = filterEditions(editions,bookArray,contributorArray,formatArray,imprintArray, true, null, null, null, null, false, null, false, seriesID)
			
			if(seriesID)
			{
				filteredEditions = FilterEditionsByIsPublished(filteredEditions, true);
			}

			if(item.limit && item.limit.value)
			{
				filteredEditions = filteredEditions.slice(0, item.limit.value)
			}
			
            return <EditionCollection
                title={item.title.value}
                editions={filteredEditions}
                buttonLink={item.button_link && item.button_link.value}
                buttonText={item.button_text && item.button_text.value}
                preferredDisplayArray={item.preferred_display && item.preferred_display.value}
                componentID={item.component_id && item.component_id.value}
                referringSlug={referringSlug}
              />
          }
          case kontent.components.types.newsletterForm : {
            return <div className="inline--newsletter"><NewsletterHelper newsletterType={linkedItem.elements.newsletter_form && linkedItem.elements.newsletter_form.value && linkedItem.elements.newsletter_form.value[0] && linkedItem.elements.newsletter_form.value[0].codename} /></div>
          }
          case kontent.components.types.productCollection : {
            const item: KontentProductCollection = linkedItem.elements;
            return <ProductCollection products={item.product_details.value} referringSlug={referringSlug} />
          }
          case kontent.components.types.quote: {
            const item: KontentQuote = linkedItem.elements;
            return <Quote quote={item.quote && item.quote.value} attribute={item.attribute && item.attribute.value} attributeLink={item.attribute_link && item.attribute_link.value} />;
          }
          case kontent.components.types.recipe: {
            const item: KontentRecipe = linkedItem.elements;
            return (
              <Recipe recipe={item} />
            );
          }
          case kontent.components.types.reusableRichText: {
            const item: KontentReusableRichText = linkedItem.elements;
            return (
              <ReusableRichText 
                richTextContent={item.content} 
                displayKeyline={item.display_keyline.value[0] && item.display_keyline.value[0].codename === "yes" ? true : false} 
              />
            );
          }
          
          case kontent.components.types.sectionBreak: {
            return <SectionBreak />;
          }
          case kontent.components.types.soundcloudEmbed: {
            const item: KontentSoundCloudEmbed = linkedItem.elements;
            return <SoundCloudEmbed id={item.soundcloud_id.value} />;
          }
          case kontent.components.types.twitterTimeline: {
            const item: KontentTwitterTimeline = linkedItem.elements;
            return <TwitterTimeline twitterTimelineUrl={item.embedded_timeline_link && item.embedded_timeline_link.value} />;
          }
          case kontent.components.types.youtubeCollection: {
            const item: KontentYouTubeCollection = linkedItem.elements;
            return (
              <YouTubeCollection
                title={item.title && item.title.value}
                introduction={item.introduction && item.introduction.value}
                itemsPerRow={item.preferred_items_per_row.value[0].codename}
                embeds={item.youtube_embeds && item.youtube_embeds.value}
                componentID={item.component_id && item.component_id.value}
              />
            );
          }
          case kontent.components.types.youtubeEmbed: {
            const item: KontentYouTubeEmbed = linkedItem.elements;
            return <YouTubeEmbed id={item.video_id.value} title={item.title.value} />;
          }
           
          default: {
            return renderCodeBlock(linkedItem);
          } 
        }
      }}
    />
  


export default KontentRichText;
